import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DataTable from '../components/ResponsiveDataTable';
import Select from 'react-select';
import { getOrders, getWarehouses, getProducts } from '../config/api'; // Import real API function
import { saveBlob, isNativeApp, toCsvCell } from '../utils/nativeFiles';
import useIsMobile from '../hooks/useIsMobile';
import CrmCallDialog from '../components/CrmCallDialog';
import CrmWhatsappDialog from '../components/CrmWhatsappDialog';
import CodeScannerDialog from '../components/CodeScannerDialog';
import { canUseCrmContacting as userCanUseCrmContacting } from '../utils/crmAccess';

const PhoneCallIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M7.2 3.5 9.6 8l-2.1 1.7a15.3 15.3 0 0 0 6.8 6.8l1.7-2.1 4.5 2.4-1 3.1c-.3.8-1 1.3-1.9 1.3A15.8 15.8 0 0 1 2.8 6.4c0-.9.5-1.6 1.3-1.9l3.1-1Z" />
  </svg>
);

const WhatsappIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 3a8.7 8.7 0 0 0-7.5 13.1L3.3 21l5-1.3A8.7 8.7 0 1 0 12 3Zm0 15.7c-1.2 0-2.4-.3-3.5-.9l-.4-.2-2.5.7.7-2.4-.2-.4A7 7 0 1 1 12 18.7Zm3.8-5.2c-.2-.1-1.2-.6-1.4-.7-.2-.1-.3-.1-.5.1l-.6.7c-.1.2-.3.2-.5.1a5.6 5.6 0 0 1-2.8-2.4c-.2-.3.2-.4.6-.9.1-.1 0-.3 0-.4L10 8.7c-.1-.3-.3-.3-.5-.3h-.4c-.2 0-.4.1-.6.3-.6.6-.9 1.3-.9 2 0 1.2.9 2.4 1 2.6a9 9 0 0 0 3.5 3.1c1.3.6 2.4.8 3.3.7.9-.1 1.8-.8 2.1-1.5.3-.7.3-1.3.2-1.4-.2-.3-.4-.4-.9-.7Z" />
  </svg>
);

const CURRENT_YEAR = new Date().getFullYear();
const ORDER_ID_PATTERN = /^[a-z0-9]+-[a-z0-9]*\d[a-z0-9]*$/i;
const EMPTY_CELL = <span className="orders-empty">—</span>;
const MIN_COLUMN_WIDTH = 60;

// '14 Oct' or '14 Oct, 3:42 pm'. The year shows only for other years: '28 Dec ’25'.
const formatOrderDate = (value, withTime = false) => {
  if (!value) return '';
  const date = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value);
  if (isNaN(date.getTime())) return '';
  const options = { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' };
  if (date.getFullYear() !== CURRENT_YEAR) options.year = '2-digit';
  let text = date.toLocaleDateString('en-IN', options).replace(/ (\d{2})$/, ' ’$1');
  if (withTime) {
    text += ', ' + date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).toLowerCase();
  }
  return text;
};

// ₹1,24,580: Indian grouping, no decimals.
const formatRupees = (amount) => '₹' + Math.round(Number(amount) || 0).toLocaleString('en-IN');

const getAmountExclGst = (order) => (Number(order.totalAmount) || 0) - (Number(order.gstAmount) || 0);

const Orders = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const hubIdFromUrl = searchParams.get('hubId');
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadAccess, setDownloadAccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Camera scan (QR / barcode). View-only: results live in memory, nothing is
  // saved to the backend. Each entry is { serial, image }.
  const [showScanner, setShowScanner] = useState(false);
  const [scannedSerials, setScannedSerials] = useState([]);
  const [scanPreview, setScanPreview] = useState(null); // { src, serial }
  const handleOrdersScan = (value, image) => {
    const serial = (value || '').trim();
    if (!serial) return;
    setScannedSerials(prev =>
      prev.some(s => s.serial === serial) ? prev : [{ serial, image }, ...prev]
    );
  };
  
  // Load filters from localStorage or use defaults
  const getInitialFilters = () => {
    const savedFilters = localStorage.getItem('ordersFilters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        // If hubId is in URL, override warehouse filter
        if (hubIdFromUrl) {
          parsed.warehouse = [hubIdFromUrl];
        }
        return parsed;
      } catch (e) {
        console.error('Error parsing saved filters:', e);
      }
    }
    return {
      search: '',
      orderId: '',
      status: [],
      confirmed: [],
      source: [],
      dateFrom: '',
      dateTo: '',
      warehouse: hubIdFromUrl ? [hubIdFromUrl] : [],
      deliveryType: [],
      product: []
    };
  };

  const [filters, setFilters] = useState(getInitialFilters());
  // One search box: an order ID (e.g. ZNX-GR39CXM4) is looked up on the server, any other text filters the loaded rows.
  const [filterText, setFilterText] = useState(() => filters.orderId || '');
  const [openFilter, setOpenFilter] = useState(null);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [currentWarehouse, setCurrentWarehouse] = useState(null);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [callTarget, setCallTarget] = useState(null);
  const [whatsappTarget, setWhatsappTarget] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
  const canUseCrmContacting = userCanUseCrmContacting(currentUser);

  // Column visibility state
  const getInitialColumnVisibility = () => {
    const savedVisibility = localStorage.getItem('ordersColumnVisibility');
    const defaultVisibility = {
      warehouse: true,
      orderId: true,
      source: true,
      customer: true,
      companyName: true,
      orderDate: true,
      status: true,
      confirmed: true,
      orderType: true,
      invoiceNumber: true,
      totalAmount: true,
      paymentMode: true,
      paymentGateway: true,
      coupon: true,
      deliveryDate: true,
      shippingDate: true,
      platform: true,
      appVersion: true,
    };
    
    if (savedVisibility) {
      try {
        const parsed = JSON.parse(savedVisibility);
        // Merge saved visibility with defaults, so new columns are visible by default
        return { ...defaultVisibility, ...parsed };
      } catch (e) {
        console.error('Error parsing saved column visibility:', e);
      }
    }
    return defaultVisibility;
  };

  const [columnVisibility, setColumnVisibility] = useState(getInitialColumnVisibility());

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('ordersColumnVisibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Column widths (desktop): drag a header edge to resize, double-click it to fit the content.
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ordersColumnWidths') || '{}');
    } catch (e) {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('ordersColumnWidths', JSON.stringify(columnWidths));
    } catch (e) {
      console.error('Error saving column widths:', e);
    }
  }, [columnWidths]);

  const startColumnResize = (event, columnKey) => {
    event.preventDefault();
    event.stopPropagation();
    const headCell = event.currentTarget.closest('.rdt_TableCol');
    if (!headCell) return;
    const startX = event.clientX;
    const startWidth = headCell.getBoundingClientRect().width;
    const onMove = (moveEvent) => {
      const width = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + moveEvent.clientX - startX));
      setColumnWidths(prev => ({ ...prev, [columnKey]: width }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.classList.remove('orders-col-resizing');
    };
    document.body.classList.add('orders-col-resizing');
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const autoFitColumn = (event, columnKey) => {
    event.preventDefault();
    event.stopPropagation();
    const headCell = event.currentTarget.closest('.rdt_TableCol');
    const table = headCell && headCell.closest('.rdt_Table');
    const columnId = headCell && headCell.getAttribute('data-column-id');
    if (!table || !columnId) return;
    // Measure each cell on one line, so wrapped text counts at its full width
    const measure = (element) => {
      const computed = window.getComputedStyle(element);
      const clone = element.cloneNode(true);
      Object.assign(clone.style, {
        position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap', width: 'auto', maxWidth: 'none',
        fontSize: computed.fontSize, fontWeight: computed.fontWeight,
      });
      document.body.appendChild(clone);
      const width = clone.getBoundingClientRect().width;
      clone.remove();
      return width;
    };
    let widest = measure(event.currentTarget.parentElement);
    table.querySelectorAll(`.rdt_TableCell[data-column-id="${columnId}"]`).forEach(cell => {
      Array.from(cell.children).forEach(child => {
        widest = Math.max(widest, measure(child));
      });
    });
    setColumnWidths(prev => ({ ...prev, [columnKey]: Math.min(480, Math.max(MIN_COLUMN_WIDTH, Math.ceil(widest) + 16)) }));
  };

  const toggleColumnVisibility = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Options for multi-select filters
  const statusOptions = [
    { value: 'ordered', label: 'Ordered' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'truncated', label: 'Truncated' }
  ];

  const deliveryTypeOptions = [
    { value: 'Delivery', label: 'Delivery' },
    { value: 'Self Pickup', label: 'Self Pickup' }
  ];

  const confirmedOptions = [
    { value: '1', label: 'Confirmed' },
    { value: '0', label: 'Not Confirmed' }
  ];

  const sourceOptions = [
    { value: 'Online', label: 'Online' },
    { value: 'Channel', label: 'Channel' },
    { value: 'ZS B2C', label: 'ZS B2C' },
    { value: 'ZS B2B', label: 'ZS B2B' },
    { value: 'hub to hub', label: 'hub to hub' },
    { value: 'damaged', label: 'damaged' }
  ];

  // Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '32px',
      border: state.isFocused ? '1px solid #0d6efd' : '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '12px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#0d6efd' : '#ced4da'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
      fontSize: '12px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#0d6efd' 
        : state.isFocused 
        ? '#f8f9fa' 
        : 'white',
      color: state.isSelected ? 'white' : '#212529',
      fontSize: '12px',
      padding: '8px 12px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#0d6efd' : '#f8f9fa'
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e7f3ff',
      fontSize: '11px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#0d6efd',
      fontSize: '11px'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#0d6efd',
      ':hover': {
        backgroundColor: '#0d6efd',
        color: 'white',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      border: '1px solid #e9ecef'
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '4px 0',
      maxHeight: '200px'
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 8px'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px',
      fontSize: '12px'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '4px'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '4px'
    })
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role == 'superAdmin') {
      setDownloadAccess(true);
    }
    if (userData.role == 'admin') {
      setIsAdmin(true);
    }
    loadWarehouses();
    loadProducts();
  }, []);

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnMenu && !event.target.closest('.column-menu-container')) {
        setShowColumnMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnMenu]);

  // Close an open filter pill on an outside click or Escape
  useEffect(() => {
    if (!openFilter) return undefined;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.orders-filter-popover') && !event.target.closest('.orders-filter-pill')) {
        setOpenFilter(null);
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') setOpenFilter(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openFilter]);

  // An order ID typed in the search box goes to the server (exact match), so orders outside the loaded set are found too
  useEffect(() => {
    const text = filterText.trim();
    const orderId = ORDER_ID_PATTERN.test(text) ? text : '';
    if (orderId === (filters.orderId || '')) return undefined;
    const timer = setTimeout(() => setFilters(prev => ({ ...prev, orderId })), 400);
    return () => clearTimeout(timer);
  }, [filterText, filters.orderId]);

  useEffect(() => {
    // Update warehouse filter when hubId is present in URL
    if (hubIdFromUrl) {
      setFilters(prev => ({
        ...prev,
        warehouse: [hubIdFromUrl]
      }));
    }
  }, [hubIdFromUrl]);

  useEffect(() => {
    loadOrders();
    // Save filters to localStorage whenever they change
    localStorage.setItem('ordersFilters', JSON.stringify(filters));
  }, [filters]);

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses();
      if (response && response.data) {
        setWarehouseOptions(response.data.map(w => ({
          value: w.id.toString(),
          label: w.name
        })));
        
        // Find and set current warehouse if hubId is present
        if (hubIdFromUrl) {
          const warehouse = response.data.find(w => w.id === parseInt(hubIdFromUrl));
          setCurrentWarehouse(warehouse);
        }
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await getProducts();
      if (response && response.data) {
        setProductOptions(response.data.map(p => ({
          value: p.id.toString(),
          label: p.name
        })));
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      // Send filters as arrays to backend
      const apiFilters = {
        ...filters,
        status: filters.status.length > 0 ? filters.status : undefined,
        confirmed: filters.confirmed.length > 0 ? filters.confirmed : undefined,
        source: filters.source.length > 0 ? filters.source : undefined,
        warehouse: filters.warehouse.length > 0 ? filters.warehouse : undefined,
        deliveryType: (filters.deliveryType || []).length > 0 ? filters.deliveryType : undefined,
        product: (filters.product || []).length > 0 ? filters.product : undefined,
        // Map orderId to order_id for backend
        order_id: filters.orderId ? filters.orderId : undefined,
      };
      
      // Remove undefined values and the old orderId key
      Object.keys(apiFilters).forEach(key => {
        if (apiFilters[key] === undefined) {
          delete apiFilters[key];
        }
      });
      delete apiFilters.orderId;
      
      const data = await getOrders(apiFilters) || {};
      setOrders(data.data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMultiSelectChange = (selectedOptions, filterName) => {
    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFilters({
      ...filters,
      [filterName]: values,
    });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: '',
      orderId: '',
      status: [],
      confirmed: [],
      source: [],
      dateFrom: '',
      dateTo: '',
      warehouse: hubIdFromUrl ? [hubIdFromUrl] : [],
      deliveryType: [],
      product: []
    };
    setFilters(clearedFilters);
    setFilterText('');
    // Clear from localStorage as well
    localStorage.setItem('ordersFilters', JSON.stringify(clearedFilters));
  };

  const handleDownloadOrders = () => {
    if (!filteredItems || filteredItems.length === 0) {
      alert('No orders to download');
      return;
    }

    try {
      // Prepare data with one row per order item
      const exportData = [];
      
      filteredItems.forEach(order => {
        if (order.items && order.items.length > 0) {
          // Create one row for each item
          order.items.forEach((item, index) => {
            exportData.push({
              // Order Information (repeated for each item)
              'Order ID': order.id,
              'Warehouse': order.warehouse_name || '',
              'Customer Name': order.customerName,
              'Customer Email': order.customerEmail || '',
              'User ID': order.userId,
              // 'Phone': order.phone || '',
              'Order Date': order.orderDate,
              'Status': order.status,
              'Confirmed': order.confirmed ? 'Yes' : 'No',
              'Amount (excl. GST)': getAmountExclGst(order).toFixed(2),
              'Amount Paid': order.advance_amount || '',
              'Payment Pending': order.pending_payment || '',
              'Source': order.source || '',
              'Platform': order.platform || '',
              'App Version': order.appVersion || '',
              'GST Number': order.gstNumber || '',
              'Company Name': order.companyName || '',
              'Order Type': (order.deliveryDay === 'self' || order.deliveryDay === 'selfTenDays') ? 'Self pickup' : 'Delivery',
              'Shipping Date': order.shippingDate || '',
              'Delivery Date': order.deliveryDate || '',
              'Payment Mode': order.paymentMode || '',
              'Shipping Address': order.deliveryAddress || '',
              // Item Information (unique for each row)
              'SKU Code': item.skuCode || '',
              'Product Name': item.name,
              'Quantity': item.quantity,
              'couponName': order.couponName || '',
              'Invoice Number': order.invoice_number || ''
            });
          });
        } else {
          // If no items, create one row with order info only
          exportData.push({
            'Order ID': order.id,
            'Warehouse': order.warehouse_name || '',
            'Customer Name': order.customerName,
            'Customer Email': order.customerEmail || '',
            'User ID': order.userId,
            // 'Phone': order.phone || '',
            'Order Date': order.orderDate,
            'Status': order.status,
            'Confirmed': order.confirmed ? 'Yes' : 'No',
            'Amount (excl. GST)': getAmountExclGst(order).toFixed(2),
            'Amount Paid': order.advance_amount || '',
            'Payment Pending': order.pending_payment || '',
            'Source': order.source || '',
            'Platform': order.platform || '',
            'App Version': order.appVersion || '',
            'GST Number': order.gstNumber || '',
            'Company Name': order.companyName || '',
            'Order Type': (order.deliveryDay === 'self' || order.deliveryDay === 'selfTenDays') ? 'Self pickup' : 'Delivery',
            'Shipping Date': order.shippingDate || '',
            'Delivery Date': order.deliveryDate || '',
            'Payment Mode': order.paymentMode || '',
            'Shipping Address': order.deliveryAddress || '',
            'SKU Code': '',
            'Product Name': '',
            'Quantity': '',
            'couponName': order.couponName || '',
            'Invoice Number': order.invoice_number || ''
          });
        }
      });

      // Convert to CSV
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.map(toCsvCell).join(','), // Header row
        ...exportData.map(row =>
          headers.map(header => toCsvCell(row[header])).join(',')
        )
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Generate filename with date
      const fileName = `Orders_Export_${new Date().toISOString().split('T')[0]}.csv`;
      
      saveBlob(blob, fileName);
      
      alert(`Successfully downloaded ${filteredItems.length} orders with ${exportData.length} total items!`);
    } catch (error) {
      console.error('Error downloading orders:', error);
      alert('Failed to download orders data');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      confirmed: 'status-badge status-confirmed',
      pending: 'status-badge status-pending',
      shipped: 'status-badge status-shipped',
      delivered: 'status-badge status-delivered',
    };
    
    return (
      <span className={statusClasses[status] || 'status-badge'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getConfirmedBadge = (confirmed) => {
    return (
      <span className={confirmed ? 'status-badge status-confirmed' : 'status-badge status-pending'}>
        {confirmed ? 'Yes' : 'No'}
      </span>
    );
  };

  // Filter data based on search text
  const filteredItems = orders.filter(
    item => {
      const searchText = filterText.trim().toLowerCase();
      return (
        item.id.toLowerCase().includes(searchText) ||
        item.customerName.toLowerCase().includes(searchText) ||
        item.customerEmail.toLowerCase().includes(searchText) ||
        item.status.toLowerCase().includes(searchText) ||
        (item.invoice_number || '').toLowerCase().includes(searchText) ||
        (item.companyName || '').toLowerCase().includes(searchText)
      );
    }
  );

  // Define all available columns
  const allColumns = useMemo(() => [
    {
      key: 'warehouse',
      name: 'Warehouse',
      selector: row => row.warehouse_name,
      sortable: true,
      width: '12%',
      cell: row => (
        <div style={{ fontWeight: '600', color: '#212529', fontSize: '12px' }}>
          {row.warehouse_name || EMPTY_CELL}
        </div>
      ),
    },
    {
      key: 'orderId',
      name: 'Order ID',
      selector: row => row.id,
      sortable: true,
      width: '8%',
      cell: row => (
        <Link 
          to={`/orders/${row.id}`}
          style={{ textDecoration: "none", color: "inherit" }}
          target='_blank'
        >
          <div style={{ fontWeight: '600', color: '#0d6efd', fontSize: '12px' }}>{row.id}</div>
        </Link>
      ),
    },
    {
      key: 'source',
      name: 'Source',
      selector: row => row.source,
      sortable: true,
      width: '8%'
    },
    {
      key: 'customer',
      name: 'Customer',
      selector: row => row.customerName,
      sortable: true,
      width: '16%',
      cell: row => (
        <div className="order-customer-cell">
          <div className="order-customer-copy">
            <div className="order-customer-name">{row.customerName}</div>
            {row.customerEmail && <div className="order-customer-email">{row.customerEmail}</div>}
          </div>
          <div className="order-customer-action-slot">
            {canUseCrmContacting && row.crmCallTarget && (
              <button
                type="button"
                className="order-contact-action order-crm-call-button"
                onClick={(event) => {
                  event.stopPropagation();
                  setCallTarget({ ...row.crmCallTarget, preferredHubId: row.warehouse_id });
                }}
                title={`Call ${row.crmCallTarget.contactName} via phone or desktop`}
                aria-label={`Call ${row.crmCallTarget.contactName}`}
              >
                <PhoneCallIcon />
              </button>
            )}
            {canUseCrmContacting && row.crmWhatsappTarget && (
              <button
                type="button"
                className="order-contact-action order-crm-whatsapp-button"
                onClick={(event) => {
                  event.stopPropagation();
                  setWhatsappTarget({ ...row.crmWhatsappTarget, preferredHubId: row.warehouse_id });
                }}
                title={`WhatsApp ${row.crmWhatsappTarget.contactName}`}
                aria-label={`WhatsApp ${row.crmWhatsappTarget.contactName}`}
              >
                <WhatsappIcon />
              </button>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'companyName',
      name: 'Company Name',
      selector: row => row.companyName,
      sortable: true,
      width: '12%',
      cell: row => (
        <div style={{ fontSize: '12px', color: '#495057' }}>
          {row.companyName || EMPTY_CELL}
        </div>
      ),
    },
    {
      key: 'orderDate',
      id: 'orderDate',
      name: 'Order Date',
      selector: row => row.orderDate,
      sortable: true,
      format: row => formatOrderDate(row.orderDate, true),
      width: '10%',
    },
    {
      key: 'status',
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      cell: row => getStatusBadge(row.status),
      width: '9%',
    },
    {
      key: 'confirmed',
      name: 'Confirmed',
      selector: row => row.confirmed,
      sortable: true,
      cell: row => getConfirmedBadge(row.confirmed),
      width: '9%',
    },
    {
      key: 'orderType',
      name: 'Order Type',
      selector: row => row.deliveryDay,
      sortable: true,
      cell: row => (
        <div style={{ fontSize: '12px', fontWeight: '500', color: '#495057' }}>
          {(row.deliveryDay === 'self' || row.deliveryDay === 'selfTenDays') ? 'Self Pickup' : 'Delivery'}
        </div>
      ),
      width: '9%',
    },
    {
      key: 'invoiceNumber',
      name: 'Invoice No.',
      selector: row => row.invoice_number,
      sortable: true,
      width: '10%',
      cell: row => (
        <div style={{ fontSize: '12px', color: '#495057', fontWeight: row.invoice_number ? '600' : '400' }}>
          {row.invoice_number || EMPTY_CELL}
        </div>
      ),
    },
    {
      key: 'totalAmount',
      name: 'Amount (excl. GST)',
      selector: row => getAmountExclGst(row),
      sortable: true,
      cell: row => (
        <div style={{ fontWeight: '600', color: '#212529', fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>
          {formatRupees(getAmountExclGst(row))}
        </div>
      ),
      right: true,
      width: '9%',
      omit: !downloadAccess
    },
    {
      key: 'paymentMode',
      name: 'Payment Mode',
      selector: row => row.paymentMode,
      sortable: true,
      width: '10%',
      cell: row => (
        <div style={{ fontSize: '12px', color: '#495057' }}>
          {row.paymentMode || EMPTY_CELL}
        </div>
      ),
    },
    {
      key: 'paymentGateway',
      name: 'Payment Gateway',
      selector: row => row.paymentGateway,
      sortable: true,
      width: '10%',
      cell: row => (
        <div style={{ fontSize: '12px', color: '#495057' }}>
          {row.paymentGateway || EMPTY_CELL}
        </div>
      ),
    },
    {
      key: 'coupon',
      name: 'Coupon',
      selector: row => row.couponName,
      sortable: true,
      width: '10%',
      cell: row => (
        <div style={{ fontSize: '12px', color: '#495057' }}>
          {row.couponName || EMPTY_CELL}
        </div>
      ),
    },
    {
      key: 'deliveryDate',
      name: 'Delivery Date',
      selector: row => row.deliveryDate,
      sortable: true,
      format: row => formatOrderDate(row.deliveryDate) || EMPTY_CELL,
      width: '10%',
    },
    {
      key: 'shippingDate',
      name: 'Shipping Date',
      selector: row => row.shippingDate,
      sortable: true,
      format: row => formatOrderDate(row.shippingDate) || EMPTY_CELL,
      width: '12%',
    },
    {
      key: 'platform',
      name: 'Platform',
      selector: row => row.platform,
      sortable: true,
      width: '10%',
      cell: row => (
        <div style={{ fontSize: '12px', color: '#495057' }}>
          {row.platform || EMPTY_CELL}
        </div>
      ),
    },
    {
      key: 'appVersion',
      name: 'App Version',
      selector: row => row.appVersion,
      sortable: true,
      width: '10%',
      cell: row => (
        <div style={{ fontSize: '12px', color: '#495057' }}>
          {row.appVersion || EMPTY_CELL}
        </div>
      ),
    },
  ], [canUseCrmContacting, downloadAccess]);

  // Filter columns based on visibility. Desktop headers get a resize handle; phones get the plain names for the cards.
  const columns = useMemo(() => {
    const visibleColumns = allColumns.filter(col => columnVisibility[col.key]);
    if (isMobile) return visibleColumns;
    return visibleColumns.map(col => ({
      ...col,
      width: columnWidths[col.key] ? `${columnWidths[col.key]}px` : col.width,
      name: (
        <span className="orders-col-head" title={col.name}>
          {col.name}
          <span
            className="orders-col-resizer"
            onMouseDown={(event) => startColumnResize(event, col.key)}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => autoFitColumn(event, col.key)}
            title="Drag to resize, double-click to fit"
          />
        </span>
      ),
    }));
  }, [allColumns, columnVisibility, columnWidths, isMobile]);

  // Filter pills: one per filter; a pill in use shows its value and an x to clear it
  const filterPills = [
    { key: 'status', label: 'Status', options: statusOptions },
    { key: 'confirmed', label: 'Confirmed', options: confirmedOptions },
    { key: 'source', label: 'Source', options: sourceOptions },
    { key: 'warehouse', label: 'Warehouse', options: warehouseOptions, locked: !!hubIdFromUrl },
    { key: 'deliveryType', label: 'Delivery type', options: deliveryTypeOptions },
    { key: 'product', label: 'Product', options: productOptions },
  ];

  const getPillValue = (pill) => {
    const labels = pill.options
      .filter(option => (filters[pill.key] || []).includes(option.value))
      .map(option => option.label);
    if (labels.length === 0) return '';
    return labels.length <= 2 ? labels.join(', ') : `${labels[0]} +${labels.length - 1}`;
  };

  const hasActiveFilters = filters.status.length > 0 || filters.confirmed.length > 0 || filters.source.length > 0 ||
    (!hubIdFromUrl && filters.warehouse.length > 0) || filters.dateFrom || filters.dateTo ||
    (filters.deliveryType || []).length > 0 || (filters.product || []).length > 0;

  const toggleFilterPopover = (event, key) => {
    if (openFilter && openFilter.key === key) {
      setOpenFilter(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setOpenFilter({ key, top: rect.bottom + 4, left: Math.max(8, Math.min(rect.left, window.innerWidth - 300)) });
  };

  const clearFilter = (event, key) => {
    event.stopPropagation();
    setFilters({ ...filters, [key]: [] });
    setOpenFilter(null);
  };

  const renderFilterPill = (key, label, value, locked) => (
    <button
      type="button"
      key={key}
      className={`orders-filter-pill${value ? ' is-set' : ''}${openFilter && openFilter.key === key ? ' is-open' : ''}`}
      onClick={(event) => !locked && toggleFilterPopover(event, key)}
      disabled={locked}
      title={locked ? 'Warehouse is locked via URL' : undefined}
    >
      {value ? (
        <>
          <span className="orders-filter-pill-key">{label}:</span>
          <span className="orders-filter-pill-value">{value}</span>
          {!locked && (
            <span className="orders-filter-pill-clear" role="button" aria-label={`Clear ${label}`} onClick={(event) => clearFilter(event, key)}>×</span>
          )}
        </>
      ) : (
        <>+ {label}</>
      )}
    </button>
  );

  const renderFilterPopover = () => {
    if (!openFilter) return null;
    const pill = filterPills.find(item => item.key === openFilter.key);
    return (
      <div className="orders-filter-popover" style={{ top: openFilter.top, left: openFilter.left }}>
        {pill && (
          <Select
            value={pill.options.filter(option => (filters[pill.key] || []).includes(option.value))}
            onChange={(selectedOptions) => handleMultiSelectChange(selectedOptions, pill.key)}
            options={pill.options}
            isMulti
            autoFocus={!isMobile}
            menuIsOpen
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            isSearchable={!isMobile || pill.options.length > 8}
            styles={{
              ...selectStyles,
              menu: (provided) => ({ ...provided, position: 'static', boxShadow: 'none', border: 0, margin: '4px 0 0' }),
              menuList: (provided) => ({ ...provided, padding: 0, maxHeight: isMobile ? '45vh' : '260px' }),
            }}
            placeholder={`Any ${pill.label.toLowerCase()}`}
            isClearable={true}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        )}
        {isMobile && (
          <button type="button" className="btn btn-primary orders-filter-done" onClick={() => setOpenFilter(null)}>
            Done
          </button>
        )}
      </div>
    );
  };

  const showTotals = downloadAccess || isAdmin;
  const totalExclGst = filteredItems.reduce((sum, order) => sum + getAmountExclGst(order), 0);

  const customStyles = {
    header: {
      style: {
        minHeight: '40px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
    },
    headRow: {
      style: {
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#dee2e6',
        backgroundColor: '#f8f9fa',
        minHeight: '36px',
      },
    },
    headCells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        backgroundColor: '#f8f9fa',
        fontWeight: '600',
        fontSize: '11px',
        color: '#495057',
        padding: '8px 6px',
        position: 'relative',
      },
    },
    cells: {
      style: {
        padding: '8px 6px',
        fontSize: '12px',
      },
    },
    rows: {
      style: {
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
        },
        borderBottomColor: '#dee2e6',
        minHeight: '40px',
      },
      stripedStyle: {
        backgroundColor: '#f8f9fa',
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #dee2e6',
        padding: '10px',
        minHeight: '40px',
        fontSize: '11px',
      },
    },
  };

  return (
    <div className="container orders-page">
      {/* Data Table Card */}
      <div className="card orders-card">
        {/* Toolbar: title, count, total, search and actions on one row */}
        <div className="orders-toolbar">
          <div className="orders-heading">
            <h3 className="orders-title">Orders</h3>
            {!loading && <span className="orders-count">{filteredItems.length}</span>}
            {hubIdFromUrl && currentWarehouse && (
              <span className="orders-hub-tag">{currentWarehouse.name}</span>
            )}
            {showTotals && filteredItems.length > 0 && (
              <span className="orders-total">
                {formatRupees(totalExclGst)} <small>excl. GST</small>
              </span>
            )}
          </div>
          <div className="orders-search">
            <input
              type="search"
              placeholder="Search order ID, customer, invoice no…"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <div className="orders-actions">
            {!isMobile && <div style={{ position: 'relative' }} className="column-menu-container">
              <button
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                className="btn btn-secondary"
                style={{ fontSize: '11px', padding: '6px 12px' }}
              >
                ⚙ Columns
              </button>
              {showColumnMenu && (
                <div style={{
                  position: 'fixed',
                  top: 'auto',
                  right: 'auto',
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 9999,
                  minWidth: '180px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  padding: '8px 0',
                  transform: 'translateY(4px)'
                }}
                ref={(el) => {
                  if (el) {
                    const button = el.previousElementSibling;
                    const rect = button.getBoundingClientRect();
                    el.style.top = `${rect.bottom}px`;
                    el.style.left = `${rect.right - el.offsetWidth}px`;
                  }
                }}
                >
                  <div style={{ 
                    padding: '6px 12px', 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: '#495057',
                    borderBottom: '1px solid #e9ecef',
                    marginBottom: '4px'
                  }}>
                    Show/Hide Columns
                  </div>
                  {allColumns.map(col => (
                    <label
                      key={col.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 12px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={columnVisibility[col.key]}
                        onChange={() => toggleColumnVisibility(col.key)}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      {col.name}
                    </label>
                  ))}
                  <button
                    type="button"
                    className="orders-reset-widths"
                    onClick={() => setColumnWidths({})}
                    disabled={Object.keys(columnWidths).length === 0}
                  >
                    Reset column widths
                  </button>
                </div>
              )}
            </div>}
            { (downloadAccess || isAdmin) ? <button
              onClick={handleDownloadOrders}
              className="btn btn-secondary"
              disabled={loading || filteredItems.length === 0}
            >
              Download
            </button> : <></> }
            <button
              onClick={() => setShowScanner(true)}
              className="btn btn-secondary"
              title="Scan QR / barcode with camera"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', lineHeight: 1.2 }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              Scan
            </button>
            <button
              onClick={() => isNativeApp() ? navigate('/orders/create') : window.open('/orders/create', '_blank')}
              className="btn btn-success"
            >
              + Create
            </button>
          </div>
        </div>

        {/* Scanned serials (view only, not saved anywhere) */}
        {scannedSerials.length > 0 && (
          <div style={{ margin: '8px 0', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                Scanned serial numbers ({scannedSerials.length})
              </span>
              <span style={{ display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: '11px', padding: '4px 10px' }}
                  onClick={() => navigator.clipboard && navigator.clipboard.writeText(scannedSerials.map(s => s.serial).join('\n'))}
                >Copy all</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: '11px', padding: '4px 10px' }}
                  onClick={() => setScannedSerials([])}
                >Clear</button>
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {scannedSerials.map(s => (
                <div key={s.serial} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                  {s.image && (
                    <img
                      src={s.image}
                      alt="scan reference"
                      title="Click to view"
                      onClick={() => setScanPreview({ src: s.image, serial: s.serial })}
                      style={{ width: '34px', height: '26px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb', cursor: 'pointer' }}
                    />
                  )}
                  <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#0369a1', fontWeight: 600 }}>{s.serial}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {showScanner && (
          <CodeScannerDialog
            open
            title="Scan serial number"
            subtitle={scannedSerials.length ? `${scannedSerials.length} scanned` : ''}
            onScan={handleOrdersScan}
            onClose={() => setShowScanner(false)}
          />
        )}

        {scanPreview && (
          <div
            onClick={() => setScanPreview(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          >
            <img
              src={scanPreview.src}
              alt="scan reference"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px' }}
            />
            <div style={{ marginTop: '12px', color: '#fff', fontSize: '13px', fontFamily: 'monospace', fontWeight: 600 }}>{scanPreview.serial}</div>
            <button
              onClick={() => setScanPreview(null)}
              style={{ marginTop: '14px', fontSize: '13px', padding: '8px 20px', background: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#111827', fontWeight: 600 }}
            >Close</button>
          </div>
        )}

        {/* Filters: From and To dates always visible, then one pill per filter */}
        <div className="orders-filter-bar">
          <div className="orders-date-range">
            <label className={filters.dateFrom ? 'is-set' : ''}>
              From
              <input type="date" name="dateFrom" value={filters.dateFrom} max={filters.dateTo || undefined} onChange={handleFilterChange} />
            </label>
            <label className={filters.dateTo ? 'is-set' : ''}>
              To
              <input type="date" name="dateTo" value={filters.dateTo} min={filters.dateFrom || undefined} onChange={handleFilterChange} />
            </label>
          </div>
          <div className="orders-filter-pills">
            {filterPills.map(pill => renderFilterPill(pill.key, pill.label, getPillValue(pill), pill.locked))}
            {hasActiveFilters && (
              <button type="button" className="orders-clear-filters" onClick={handleClearFilters}>
                Clear all
              </button>
            )}
          </div>
        </div>
        {renderFilterPopover()}

        <DataTable
          columns={columns}
          data={filteredItems}
          progressPending={loading}
          pagination
          paginationResetDefaultPage={false}
          selectableRows={false}
          customStyles={customStyles}
          highlightOnHover
          pointerOnHover
          responsive
          striped
          paginationPerPage={10}
          paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
          noDataComponent={
            <div style={{ 
              padding: '60px 40px', 
              textAlign: 'center',
              color: '#6c757d',
              fontSize: '16px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>No orders found</div>
              <div>Try adjusting your search criteria or filters</div>
            </div>
          }
          defaultSortFieldId="orderDate"
          defaultSortAsc={false}
          mobileSummaryFields={['Order ID', 'Customer', 'Warehouse', 'Status', 'Amount (excl. GST)', 'Order Date']}
        />
      </div>

      {callTarget && (
        <CrmCallDialog
          leadId={callTarget.leadId}
          contact={{ id: callTarget.contactId, name: callTarget.contactName }}
          hubLinks={callTarget.hubLinks || []}
          preferredHubId={callTarget.preferredHubId}
          orderId={callTarget.orderId}
          onClose={() => setCallTarget(null)}
          onLogged={() => setCallTarget(null)}
        />
      )}
      {whatsappTarget && (
        <CrmWhatsappDialog
          target={whatsappTarget}
          preferredHubId={whatsappTarget.preferredHubId}
          onClose={() => setWhatsappTarget(null)}
        />
      )}
    </div>
  );
};

export default Orders;
